// Modals shim — enqueued universally.
//
// The React modal app (DynamicModal + PageViewModal) is loaded lazily via
// a dynamic import so that React is never downloaded on pages that never
// open a modal. The shim is intentionally tiny (no React, no framework)
// and uses document-level event delegation so it catches every trigger
// regardless of when/how the trigger element enters the DOM (initial
// render, FacetWP refresh, any future AJAX-inserted content).
//
// Known trigger surfaces (keep in sync with ModalsApp):
//   DynamicModal:
//     - URL hash matching /^#modal:/
//     - URL search param `?modal=<slug>`
//     - Clicks on `[href*="#modal:"]`
//   PageViewModal:
//     - Clicks on `[data-page-view-modal]`
//
// If a new modal trigger API is added, update `MODAL_TRIGGER_SELECTOR`
// and, if it's URL-based, `urlWantsModal()` below.

const MODAL_TRIGGER_SELECTOR = '[href*="#modal:"], [data-page-view-modal]';

let bootPromise = null;

// Resolves only after the React app has mounted AND its effects have
// fired (i.e. the app's own click/hash listeners are attached). Awaiting
// this before replaying a click guarantees the replay lands on a live
// listener.
function boot() {
  if (!bootPromise) {
    bootPromise = import(
      /* webpackChunkName: "modals-app" */ './apps/ModalsApp'
    ).then((mod) => mod.default());
  }
  return bootPromise;
}

function urlWantsModal() {
  if (/^#modal:/.test(window.location.hash)) return true;
  try {
    return new URLSearchParams(window.location.search).has('modal');
  } catch (e) {
    return false;
  }
}

function init() {
  if (urlWantsModal()) boot();

  window.addEventListener('hashchange', () => {
    if (urlWantsModal()) boot();
  });

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest(MODAL_TRIGGER_SELECTOR);
      if (!trigger) return;

      // Let the real app claim the click once it's mounted.
      if (document.getElementById('app-modals')?.dataset.modalsMounted === '1') {
        return;
      }

      // Prevent the browser from following the href (would jump to a hash
      // that doesn't exist) and re-dispatch after the app mounts so the
      // app's own click handler runs.
      event.preventDefault();
      event.stopPropagation();

      boot().then(() => {
        // Replay the click; the now-mounted app will handle it.
        trigger.click();
      });
    },
    // Capture phase so we run before any app-level delegates, but only
    // intercept while the app isn't mounted yet.
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
